import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { getAllHelpEntries } from '../data/helpEntries';
import { Badge } from '../components/ui/Badge';
import { Difficulty } from '../types/lab';

const difficultyOptions: { label: string; value: Difficulty | 'all' }[] = [
  { label: 'All difficulties', value: 'all' },
  { label: 'Beginner', value: Difficulty.BEGINNER },
  { label: 'Intermediate', value: Difficulty.INTERMEDIATE },
  { label: 'Advanced', value: Difficulty.ADVANCED },
];

function getDifficultyColor(difficulty: Difficulty) {
  switch (difficulty) {
    case Difficulty.BEGINNER:
      return 'bg-green-500';
    case Difficulty.INTERMEDIATE:
      return 'bg-yellow-500';
    case Difficulty.ADVANCED:
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
}

function formatDifficultyLabel(difficulty: Difficulty) {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}

export function HelpLanding() {
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<Difficulty | 'all'>('all');

  const entries = useMemo(() => getAllHelpEntries(), []);

  const filteredEntries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return entries.filter(entry => {
      const matchesDifficulty = difficultyFilter === 'all' || entry.difficulty === difficultyFilter;
      const matchesSearch =
        normalizedSearch.length === 0 ||
        entry.title.toLowerCase().includes(normalizedSearch) ||
        entry.shortDescription.toLowerCase().includes(normalizedSearch) ||
        entry.concepts.some(concept => concept.toLowerCase().includes(normalizedSearch));

      return matchesDifficulty && matchesSearch;
    });
  }, [entries, searchTerm, difficultyFilter]);

  return (
    <div className="bg-gray-900 text-white min-h-screen p-6 sm:p-10">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10">
          <h1 className="text-4xl font-bold mb-3">Help</h1>
          <p className="text-gray-300 text-lg max-w-3xl">
            Lab setup notes, troubleshooting hints, and reference docs. These pages explain the tools and workflows without giving away lab answers.
          </p>
        </header>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="relative w-full md:max-w-md">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search labs…"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 pr-10 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-500" aria-hidden="true" />
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="difficulty-filter" className="text-sm text-gray-400">
              Difficulty
            </label>
            <select
              id="difficulty-filter"
              value={difficultyFilter}
              onChange={(event) => setDifficultyFilter(event.target.value as Difficulty | 'all')}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficultyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filteredEntries.length === 0 ? (
          <p className="text-gray-400">No labs match your filters. Adjust your search or difficulty selection.</p>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map(entry => (
              <Link
                key={entry.slug}
                to={`/help/${entry.slug}`}
                className="block rounded-lg border border-gray-800 bg-gray-800 hover:border-blue-600 transition-colors"
              >
                <div className="p-5 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <h2 className="text-2xl font-semibold leading-tight">{entry.title}</h2>
                    <Badge className={`${getDifficultyColor(entry.difficulty)} text-white`}>{formatDifficultyLabel(entry.difficulty)}</Badge>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{entry.shortDescription}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {entry.concepts.slice(0, 4).map(concept => (
                      <span key={concept} className="rounded-md bg-gray-900 px-2 py-1 text-xs text-gray-400">
                        {concept}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 text-sm text-blue-400 flex items-center gap-2">
                    <span>Open help</span>
                    <span aria-hidden="true">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HelpLanding;
